/* eslint-disable no-sparse-arrays */
import React, {
	memo,
	useCallback,
	useEffect,
	useMemo,
	useState
} from 'react';
import {
	Link,
	useLocation,
	useParams
} from 'react-router-dom';
import {
	Header,
	Tab
} from 'semantic-ui-react';

import { log } from '../../../../../services/utils';

import useForm from '../../../../../hooks/useForm';

import {
	getProgram,
	updateProgram,
	createProgram
} from '../../../../../api/nora/programs';

import {
	useProgramState,
	useProgramAction
} from '../../../../../contexts/ProgramsContext';
import { useUIAction } from '../../../../../contexts/UIContext';

import Alert from '../../../../Alert';
import CoordinationTab from './CoordinationTab';
import InfoTab from './InfoTab';
import SubmissionsTab from './SubmissionsTab';

import TemplatesTab from './TemplatesTab';
import ParticipantsTab from './ParticipantsTab';
import OverviewTab from './OverviewTab';

const ProgramDetails = () => {
	const { id } = useParams();
	const { state: navigationState } = useLocation();

	const {
		override,
		program,
		initialized
	} = useProgramState();
	const { setProgram } = useProgramAction();
	const {
		setApiErrorAlert,
		setInfobar,
		setInfobarLoader,
		setNavLock
	} = useUIAction();

	const [ isLoading, setIsLoading ] = useState( true );
	const [ forceValidation, setForceValidation ] = useState( false );
	const [ activeIndex, setActiveIndex ] = useState( navigationState
		? navigationState.activeIndex
		: 0 );
	const [ unsavedProgressAlert, setUnsavedProgressAlert ] = useState( false );
	const [ programSavedAlert, setProgramSavedAlert ] = useState( false );
	// used to store the tab index when showing the unsavedProgressAlert
	const [ pendingIndex, setPendingIndex ] = useState();

	const {
		formData,
		hasChanged: formDataHasChanged,
		updateField,
		resetFormData,
		setFormData
	} = useForm();

	useEffect( () => {
		navigationState?.to
			? setInfobar( <span><Link to={ navigationState.to }>{ navigationState.text || '\u2190\u00A0\u00A0Back' }</Link></span> )
			: override.breadcrumb
				? setInfobar( override.breadcrumb )
				: setInfobar( <span>Operations &nbsp;&#9656;&nbsp; <Link to="/operations/programs">Programs</Link> &nbsp;&#9656;&nbsp; { program?.name ? program.name : 'New Program' }</span> );
	}, [
		id,
		navigationState,
		override.breadcrumb,
		program,
		setInfobar
	] );

	useEffect( () => {
		if ( navigationState?.activeIndex ) {
			setActiveIndex( navigationState?.activeIndex );
		}
	}, [ navigationState?.activeIndex ] );

	useEffect( () => {
		if ( !initialized ) {
			setInfobarLoader( 'Please wait... ' );
		} else if ( id > 0 || override.id ) { // not a new program
			( async () => {
				const infobarLoader = override.infobarLoader
					? override.infobarLoader
					: 'Retrieving Program Info...';

				setInfobarLoader( infobarLoader );

				try {
					const programId = override.id ?? id;

					const response = await getProgram( programId );

					setProgram( response );
				} catch ( error ) {
					setApiErrorAlert( error );
				}
			} )();
		}
	}, [
		id,
		initialized,
		override.id,
		override.infobarLoader,
		setProgram,
		setInfobarLoader,
		setApiErrorAlert
	] );

	useEffect( () => {
		if ( formDataHasChanged ) {
			setNavLock( true );
		} else {
			setNavLock( false );
		}
	}, [
		formDataHasChanged,
		setNavLock
	] );

	useEffect( () => {
		if ( program ) {
			setFormData( {
				...program,
				submissionStatus: program.submissionStatus.id,
				type: program.type.id
			} );
		} else if ( id === '0' ) {
			setFormData( { id: 0 } );
		}
	}, [
		id,
		program,
		setFormData
	] );

	useEffect( () => {
		if ( initialized && Object.keys( formData ).length ) {
			setIsLoading( false );
			setInfobarLoader( '' );
		}
	}, [
		formData,
		initialized,
		setInfobarLoader
	] );

	const errors = useMemo( () => ( {
		name: [
			{
				error: !formData.name,
				message: 'Program Name is a required field.',
				fieldLabel: 'Program Name'
			}
		],
		type: [
			{
				error: !formData.type,
				message: 'Program Type is a required field.',
				fieldLabel: 'Program Type'
			}
		],
		startDate: [
			{
				error: !formData.startDate,
				message: 'Start Date is a required field.',
				fieldLabel: 'Start Date'
			},
			{
				error: new Date( formData.endDate ) <= new Date( formData.startDate ),
				message: 'Start Date must procede End Date by at least one calendar day.',
				fieldLabel: 'Start Date'
			}
		],
		endDate: [
			{
				error: !formData.endDate,
				message: 'End Date is a required field.',
				fieldLabel: 'End Date'
			},
			{
				error: new Date( formData.endDate ) <= new Date( formData.startDate ),
				message: 'End Date must suceed Start Date by at least one calendar day.',
				fieldLabel: 'End Date'
			}
		],
		submissionStatus: [
			{
				error: !formData.submissionStatus,
				message: 'Program Status is a required field.',
				fieldLabel: 'Program Status'
			}
		]
	} ), [
		formData.endDate,
		formData.name,
		formData.startDate,
		formData.submissionStatus,
		formData.type
	] );

	const validate = useCallback( () => Object.values( errors ).some( errs => errs.some( err => err.error ) ), [ errors ] );

	const handleSave = useCallback( async cb => {
		if ( validate() ) {
			if ( !forceValidation ) {
				setForceValidation( true );
			}
		} else {
			setForceValidation( false );

			let {
				submissionStatus,
				type,
				...rest
			} = formData;
			const data = {
				...rest,
				submissionStatus: { id: submissionStatus },
				type: { id: type }
			};

			try {
				setNavLock( false );

				if ( formData.id === 0 ) {
					const response = await createProgram( data );

					setProgram( response );
				} else {
					const response = await updateProgram( data );

					setProgram( response );
				}

				setProgramSavedAlert( true );

				cb?.();
			} catch ( error ) {
				setNavLock( true );
				setApiErrorAlert( error );
			}
		}
	}, [
		forceValidation,
		formData,
		setApiErrorAlert,
		setNavLock,
		setProgram,
		validate
	] );

	const menu = useMemo( () => ( {
		className: 'no-print',
		pointing: true,
		secondary: true,
		floated: true,
		style: {
			width: '100%',
			paddingTop: '0.5rem',
			marginBottom: '3rem'
		}
	} ), [] );

	const renderInfoTab = useCallback( () => (
		<InfoTab
			errors={ errors }
			forceValidation={ forceValidation }
			formData={ formData }
			formDataHasChanged= {formDataHasChanged }
			handleSave={ handleSave }
			resetFormData={ resetFormData }
			updateField={ updateField }
			validate={ validate }
		/>
	), [
		errors,
		forceValidation,
		formData,
		formDataHasChanged,
		handleSave,
		resetFormData,
		updateField,
		validate
	] );

	const renderCoordinationTab = useCallback( ( { activeIndex } ) => <CoordinationTab activeIndex={ activeIndex } />, [] );

	const renderParticipantsTab = useCallback( ( { activeIndex } ) => <ParticipantsTab activeIndex={ activeIndex } />, [] );

	const renderTemplatesTab = useCallback( () => <TemplatesTab />, [] );

	const renderSubmissionsTab = useCallback( () => <SubmissionsTab />, [] );

	const renderOverviewTab = useCallback( () => <OverviewTab />, [] );

	const renderPanes = useCallback( () => {
		let panes = [
			...!override.isSystem
				? [
					{
						menuItem: {
							key: 'info',
							content: 'Info'
						},
						render: renderInfoTab
					},
					{
						menuItem: {
							key: 'coordination',
							content: 'Coordination'
						},
						render: renderCoordinationTab
					},
					{
						menuItem: {
							key: 'agencies',
							content: 'Agencies'
						},
						render: renderParticipantsTab
					}
				]
				: [],
			{
				menuItem: {
					key: 'overview',
					content: 'Overview'
				},
				render: renderOverviewTab
			},
			{
				menuItem: {
					key: 'templates',
					content: 'Templates'
				},
				render: renderTemplatesTab
			},
			{
				menuItem: {
					key: 'submissions',
					content: 'Submissions'
				},
				render: renderSubmissionsTab
			}
		];

		return panes;
	}, [
		override.isSystem,
		renderCoordinationTab,
		renderTemplatesTab,
		renderInfoTab,
		renderOverviewTab,
		renderParticipantsTab,
		renderSubmissionsTab
	] );

	const handleUnsavedProgress = useCallback( async () => {
		await handleSave();
		setUnsavedProgressAlert( false );
		setActiveIndex( pendingIndex );
	}, [
		handleSave,
		pendingIndex
	] );

	const closeUnsavedProgressAlert = useCallback( () => {
		setUnsavedProgressAlert( false );
	}, [] );

	const closeProgramSavedAlert = useCallback( () => {
		setProgramSavedAlert( false );
	}, [] );

	const handleTabChange = useCallback( ( e, { activeIndex } ) => {
		if ( override.isSystem ) {
			setActiveIndex( activeIndex );

			return;
		}

		if ( !validate() ) {
			if ( forceValidation ) {
				setForceValidation( false );
			}

			if ( formDataHasChanged ) {
				setPendingIndex( activeIndex );
				setUnsavedProgressAlert( true );
			} else {
				setActiveIndex( activeIndex );
			}
		} else if ( !forceValidation ) {
			setForceValidation( true );
		}
	}, [
		forceValidation,
		formDataHasChanged,
		override.isSystem,
		validate
	] );

	if ( isLoading ) {
		return null;
	}

	return (
		<div className="detail-page">
			{ log( 'programDetails' ) }
			<Tab
				activeIndex={ activeIndex }
				menu={ menu }
				onTabChange={ handleTabChange }
				panes={ renderPanes() }
			/>
			<Alert
				action={ handleUnsavedProgress }
				actionText="Save and continue"
				name="program-unsaved-progress-confirm-alert"
				message="Please save your progress before continuing."
				open={ unsavedProgressAlert }
				closeModal={ closeUnsavedProgressAlert }
				closeText="Go back"
				type="confirm"
			/>
			<Alert
				closeModal={ closeProgramSavedAlert }
				message={ <Header as="h1" color="pink">Saved!</Header> }
				name="program-saved-notification"
				open={ programSavedAlert }
				type="notification"
			/>
		</div>
	);
};

export default memo( ProgramDetails );
